import styled from "styled-components";
import { motion } from "framer-motion";
import {
  PageActions,
  PageContainer,
  PageState as SharedPageState,
  PageTitle,
  PageTopBar,
  SectionTitle,
  getToneTokens,
} from "../../styles/shared";
import { mq } from "../../styles/canonical/breakpoints";
import { kpiFaixaCss, kpiTileCss } from "../../components/ListaCompacta/styles";

const PISO = "var(--text-min, 12px)";

export const Wrapper = styled(PageContainer)`
  gap: 14px;
  /* Largura toda: tabela, calendário e histórico precisam do espaço (o teto
     de 1560px do mockup deixava faixas vazias em telas largas). */
  max-width: none;
`;

export const PageState = styled(SharedPageState)``;

export const TopBar = styled(PageTopBar)``;

export const Titulo = styled(PageTitle)``;

export const TopBarActions = styled(PageActions)`
  @media ${mq.tablet} {
    flex-wrap: nowrap;
    gap: 6px;
  }
`;

/* FilterPanel (compartilhado) não envolve o texto do gatilho em .rotulo:
   abaixo de 1024px os botões da barra (Filtros / Limpar) viram só ícone
   escondendo o texto por font-size, sem afetar o dropdown aberto. */
export const FiltroSoIcone = styled.div`
  display: contents;

  @media ${mq.tablet} {
    > * > * > button {
      width: 36px;
      min-height: 36px;
      height: 36px;
      padding: 0;
      gap: 0;
      font-size: 0;
      justify-content: center;
    }
  }
`;

export const SecaoTitulo = styled(SectionTitle)``;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
  gap: 12px;
  ${kpiFaixaCss}
`;

/* KPI card do board — estrutura do mockup (glass + label/valor na mesma
   linha de base + sub embaixo), com a paleta e o tema do ERP. Grid mantém
   a marcação plana (label, valor, sub). */
export const SummaryCard = styled.div`
  border-radius: 13px;
  padding: 12px 15px;
  background: linear-gradient(
    155deg,
    ${({ theme }) => theme.surfaceAlt},
    ${({ theme }) => theme.surface}
  );
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(13px) saturate(1.3);
  -webkit-backdrop-filter: blur(13px) saturate(1.3);
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 8px;
  align-items: baseline;

  /* Export (html2canvas ignora blur): gradiente glass viraria duas faixas
     de cor no JPG/PNG — achata para superficie solida so na captura. */
  body[data-export-mode="true"] & {
    background: ${({ theme }) => theme.surfaceAlt};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  ${kpiTileCss}
`;

export const SummaryLabel = styled.div.attrs({ className: "kpi-label" })`
  grid-column: 1;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.textTertiary};
`;

export const SummaryValue = styled.div.attrs({ className: "kpi-num" })`
  grid-column: 2;
  justify-self: end;
  font-family: var(--font-display);
  font-size: 23px;
  line-height: 1;
  font-weight: 600;
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
  overflow-wrap: anywhere;
`;

export const SummarySub = styled.div.attrs({ className: "kpi-sub" })`
  grid-column: 1 / -1;
  margin-top: 8px;
  font-size: 10.5px;
  color: ${({ theme }) => theme.textTertiary};
`;

export const StatusBadge = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;

  /* Selo do card compacto: não pode inflar o card de 76px. */
  @media ${mq.tablet} {
    white-space: nowrap;
    min-height: 22px;
    padding: 2px 8px;
  }
`;

export const EmptyState = styled.div`
  padding: 24px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 220;
  background: rgba(15, 15, 20, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const ModalCard = styled(motion.div)`
  width: min(1120px, 100%);
  max-height: min(86vh, 920px);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ModalMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const ModalCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
  }

  @media ${mq.tablet} {
    width: var(--tap-min, 44px);
    height: var(--tap-min, 44px);
  }
`;

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px 20px 20px;
  overflow: auto;
`;

export const ModalInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 12px;
`;

export const ModalInfoCard = styled.div`
  border-radius: 12px;
  padding: 14px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
`;

export const ModalInfoLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 6px;

  @media ${mq.tablet} {
    font-size: ${PISO};
  }
`;

export const ModalInfoValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
`;

/* Lista de ambientes do lote em CARDS (não tabela).
   Bugfix 2026-05-31: a tabela antiga tinha 9 colunas (min-width 980px) e
   estourava o painel de ~720px — colunas da direita ficavam cortadas e
   ilegíveis (sem scroll, viola o "zero scroll lateral" do PharuERP). Cards
   com grid auto-fit quebram naturalmente dentro da largura do painel. */

export const AmbientesLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AmbienteCard = styled.div`
  border-radius: 12px;
  padding: 14px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
`;

export const AmbienteCardTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

export const AmbienteNome = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const AmbienteDatasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 116px), 1fr));
  gap: 10px 14px;
`;

export const DatoCampo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const DatoLabel = styled.span`
  font-size: 10px;
  line-height: 1.2;
  /* Reserva ~2 linhas: labels longos ("Entrega Prevista") quebram em vez
     de truncar, e os valores ficam alinhados entre os campos da grade. */
  min-height: 2.4em;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};

  @media ${mq.tablet} {
    font-size: ${PISO};
  }
`;

export const DatoValor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, $muted }) => ($muted ? theme.textSecondary : theme.textPrimary)};
`;

/* =============================================================
 *  Visões: Kanban · Tabela · Calendário · Histórico
 * ============================================================= */

export const VisaoBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  @media ${mq.tablet} {
    gap: 6px;
  }
`;

/* Abaixo de 1024px: uma linha rolável de chips (nunca quebra nem estoura). */
export const VisaoTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};

  @media ${mq.tablet} {
    display: flex;
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const VisaoTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 6px 12px;
  border: 0;
  border-radius: 8px;
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.textPrimary : theme.textSecondary)};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;

  @media ${mq.tablet} {
    flex: 0 0 auto;
    min-height: 36px;
  }

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const VisaoControles = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ToggleCheck = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;

  @media ${mq.tablet} {
    min-height: 36px;

    input {
      width: 20px;
      height: 20px;
    }
  }

  em {
    font-style: normal;
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

/* -- Tabela (zero scroll lateral: layout fixo + ellipsis com tooltip) -- */

export const TabelaWrap = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow-x: hidden;
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
`;

export const Th = styled.th`
  padding: 9px 10px;
  text-align: ${({ $align }) => $align || "left"};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${mq.tablet} {
    padding: 7px 6px;
    font-size: ${PISO};
    letter-spacing: 0.02em;
  }
`;

export const ThBotao = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

export const Td = styled.td`
  padding: 8px 10px;
  text-align: ${({ $align }) => $align || "left"};
  color: ${({ theme, $muted }) => ($muted ? theme.textSecondary : theme.textPrimary)};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${mq.tablet} {
    padding: 7px 6px;
  }
`;

export const LinhaClicavel = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const CelulaDupla = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  strong {
    font-weight: 600;
  }
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const Progresso = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  small {
    min-width: 34px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const ProgressoTrilho = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  overflow: hidden;

  > div {
    height: 100%;
    border-radius: 999px;
    background: ${({ theme, $completo }) =>
      getToneTokens(theme, $completo ? "success" : "info").accent};
  }
`;

export const AguardandoTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px dashed ${({ theme }) => getToneTokens(theme, "warning").border};
  color: ${({ theme }) => getToneTokens(theme, "warning").text};
  font-size: 12px;
  font-weight: 600;
`;

/* -- Histórico -- */

export const HistoricoGrid = styled.div`
  display: grid;
  gap: 14px;
`;

export const Painel = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const PainelTitulo = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};

  small {
    margin-left: 8px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }
`;

export const Multiplos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 14px;
`;

export const MultiploTitulo = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};

  span {
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const DuasColunas = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 520px), 1fr));
  gap: 14px;
`;

export const TooltipBox = styled.div`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);

  strong {
    display: block;
    margin-bottom: 2px;
  }
`;

/* Calendário (componente compartilhado): garante que nada estoure a
   largura da página abaixo de 1024px. */
export const CalendarioWrap = styled.div`
  min-width: 0;

  @media ${mq.tablet} {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

/* Meta do card de lote no kanban/tabela: C/F/B com o estado da operação. */
export const OpMeta = styled.span`
  font-weight: 700;
  color: ${({ theme, $tom }) =>
    $tom === "success"
      ? getToneTokens(theme, "success").text
      : $tom === "proxima"
        ? theme.textPrimary
        : theme.textTertiary};
`;

export const BotaoHistorico = styled.button`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--tap-min, 32px);
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`;
